import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createInvestor,
  listInvestors,
  updateInvestor,
  type Investor,
  type InvestorStage,
  type InvestorType,
} from "@/lib/api";
import {
  INVESTOR_STAGE_COLOR,
  INVESTOR_STAGE_COLUMNS,
  INVESTOR_TYPE_LABEL,
  fmtMoney,
  fmtPct,
  isOverdue,
  isParked,
  summarise,
} from "@/lib/investors";
import { Kanban } from "@/components/Kanban";
import { Modal } from "@/components/Modal";
import { EntityLogo } from "@/components/EntityLogo";
import { InvestorDrawer } from "@/components/crm/InvestorDrawer";

/**
 * Our own fundraising board. Reads like the sales pipeline on purpose, but
 * the money flows the other way, so it is a separate record end to end —
 * an investment must never turn up in open-pipeline value.
 */
export function InvestorsPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [openInvestor, setOpenInvestor] = useState<Investor | null>(null);
  const [typeFilter, setTypeFilter] = useState<InvestorType | "all">("all");

  const investorsKey = ["investors"] as const;
  const { data: investors } = useQuery({ queryKey: investorsKey, queryFn: listInvestors });

  const move = useMutation({
    mutationFn: (vars: { id: string; stage: InvestorStage }) =>
      updateInvestor(vars.id, { stage: vars.stage }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: investorsKey });
      const prev = qc.getQueryData<Investor[]>(investorsKey);
      qc.setQueryData<Investor[]>(investorsKey, (old) =>
        old?.map((i) => (i.id === vars.id ? { ...i, stage: vars.stage } : i)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(investorsKey, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: investorsKey }),
  });

  const all = useMemo(() => investors ?? [], [investors]);
  // Different types run different processes; filtering keeps the board legible.
  const shown = typeFilter === "all" ? all : all.filter((i) => i.type === typeFilter);
  // Rollups always read the whole raise, never the filtered slice.
  const summary = useMemo(() => summarise(all), [all]);

  // The drawer edits a snapshot, so remount it when a different investor opens.
  const openRow = openInvestor
    ? all.find((i) => i.id === openInvestor.id) ?? openInvestor
    : null;

  return (
    <div>
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title">Investors</h1>
          <p className="mt-2 text-text-secondary">
            {summary.liveCount
              ? `${summary.liveCount} live conversation${summary.liveCount === 1 ? "" : "s"} · drag as they progress.`
              : "Everyone who might back us, and where each conversation stands."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as InvestorType | "all")}
            className="rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text-secondary outline-none focus:border-accent"
          >
            <option value="all">All types</option>
            {Object.entries(INVESTOR_TYPE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setAddOpen(true)}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ color: "var(--color-on-accent)" }}
          >
            <i className="fa-solid fa-plus mr-1.5 text-xs" />
            New investor
          </button>
        </div>
      </header>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Committed" value={fmtMoney(summary.committed)} tone="good" />
        <Stat
          label="Live"
          value={fmtMoney(summary.liveTotal)}
          hint={`${fmtMoney(summary.weighted)} weighted by confidence`}
        />
        <Stat
          label="Equity promised"
          value={fmtPct(summary.promisedDilution)}
          hint="across term sheets and commitments"
          tone={summary.promisedDilution > 25 ? "warn" : undefined}
        />
        <Stat
          label="Overdue next steps"
          value={String(summary.overdue)}
          hint={summary.overdue ? "these are the ones going quiet" : "nothing slipping"}
          tone={summary.overdue ? "warn" : undefined}
        />
      </div>

      <Kanban
        columns={INVESTOR_STAGE_COLUMNS.map((c) => ({
          key: c.stage,
          label: c.label,
          color: INVESTOR_STAGE_COLOR[c.stage],
        }))}
        items={shown}
        idOf={(i) => i.id}
        columnOf={(i) => i.stage}
        onMove={(id, stage) => move.mutate({ id, stage })}
        onCardClick={setOpenInvestor}
        sortItems={(a, b) => (b.amount ?? 0) - (a.amount ?? 0)}
        columnFooter={(items) => {
          const total = items.reduce((s, i) => s + (i.amount ?? 0), 0);
          return total ? <span>{fmtMoney(total)}</span> : null;
        }}
        renderCard={(i) => <InvestorCard investor={i} />}
      />

      {addOpen && <NewInvestorModal onClose={() => setAddOpen(false)} />}
      {openRow && (
        <InvestorDrawer
          key={openRow.id}
          investor={openRow}
          onClose={() => setOpenInvestor(null)}
        />
      )}
    </div>
  );
}

function InvestorCard({ investor }: { investor: Investor }) {
  const overdue = isOverdue(investor.nextStepOn);
  const parked = isParked(investor);

  return (
    <>
      <div className="flex items-start gap-2">
        <EntityLogo
          name={investor.name}
          url={investor.logoUrl}
          size={22}
          fallbackIcon="fa-sack-dollar"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-snug text-text-heading">
            {investor.name}
          </p>
          <p className="truncate text-[11px] text-text-muted">
            {INVESTOR_TYPE_LABEL[investor.type]}
            {investor.contactName && ` · ${investor.contactName}`}
          </p>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="truncate text-[11px] text-text-muted">
          {investor.equityPct != null ? `for ${fmtPct(investor.equityPct)}` : ""}
        </span>
        {investor.amount != null && (
          <span className="shrink-0 text-[11px] font-semibold text-text-secondary">
            {fmtMoney(investor.amount)}
          </span>
        )}
      </div>

      {(overdue || parked || investor.nextStep) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {overdue && (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold text-danger ring-1 ring-inset ring-danger/40">
              <i className="fa-solid fa-clock mr-1" />
              Overdue
            </span>
          )}
          {parked && (
            <span className="rounded px-1.5 py-0.5 text-[10px] text-text-muted ring-1 ring-inset ring-border">
              Revisit later
            </span>
          )}
          {investor.nextStep && !overdue && !parked && (
            <span className="truncate text-[10px] text-text-muted">{investor.nextStep}</span>
          )}
        </div>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "good" | "warn";
}) {
  const valueColor =
    tone === "good" ? "text-accent" : tone === "warn" ? "text-danger" : "text-text-heading";
  return (
    <div className="rounded-token-lg border border-border bg-bg-card px-4 py-3">
      <p className="text-xs text-text-muted">{label}</p>
      <p className={`mt-0.5 font-display text-xl font-semibold ${valueColor}`}>{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-text-muted">{hint}</p>}
    </div>
  );
}

function NewInvestorModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState<InvestorType>("angel");
  const [stage, setStage] = useState<InvestorStage>("lead");
  const [amount, setAmount] = useState("");
  const [equityPct, setEquityPct] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createInvestor({
        name,
        type,
        stage,
        amount: amount ? Number(amount) : null,
        equityPct: equityPct ? Number(equityPct) : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["investors"] });
      onClose();
    },
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title="New investor">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Name</label>
        <input
          className={inputCls}
          value={name}
          placeholder="Firm or person"
          onChange={(e) => setName(e.target.value)}
          required
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Type</label>
            <select
              className={inputCls}
              value={type}
              onChange={(e) => setType(e.target.value as InvestorType)}
            >
              {Object.entries(INVESTOR_TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Stage</label>
            <select
              className={inputCls}
              value={stage}
              onChange={(e) => setStage(e.target.value as InvestorStage)}
            >
              {INVESTOR_STAGE_COLUMNS.map((s) => (
                <option key={s.stage} value={s.stage}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Amount (£)</label>
            <input
              className={inputCls}
              type="number"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Equity (%)</label>
            <input
              className={inputCls}
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={equityPct}
              onChange={(e) => setEquityPct(e.target.value)}
            />
          </div>
        </div>

        <p className="-mt-1 mb-3 text-xs text-text-muted">
          The rest — instrument, length of support, conditions — is on the card once created.
        </p>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending || !name.trim()}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Creating…" : "Add investor"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
